import { Box, Text } from '@medly-components/core';
import { centerAligned, getFontStyle } from '@medly-components/utils';
import { rgba } from 'polished';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import grey from '../../../theme/colors/grey';

interface FlexBoxProps {
    direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    rowGap?: string;
    colGap?: string;
    maxWidth?: string;
    maxWidthDirection?: string;
    alignItems?: string;
    flexWrap?: string;
}

export const scrollbarStyle = css`
    &::-webkit-scrollbar {
        width: 0.5rem;
        height: 0.5rem;
    }
    &::-webkit-scrollbar-track {
        background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
        border-radius: 1rem;
        background-color: ${({ theme }) => theme.colors.grey[500]};
    }
`;

export const LoaderContainer = styled(Box)`
    width: fit-content;
    margin: auto;
`;

export const Overlay = styled(Box)`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 2;
    cursor: pointer;
`;

export const NoBgBox = styled(Box)`
    background: none;
`;

export const FlexBox = styled(Box)<FlexBoxProps>`
    background: none;
    display: flex;
    padding: 0;
    margin: 0;
    flex-direction: ${props => props.direction || 'row'};
    align-items: ${props => props.alignItems};
    row-gap: ${props => props.rowGap};
    column-gap: ${props => props.colGap};
    flex-wrap: ${props => props.flexWrap};
    @media (max-width: ${props => props.maxWidth}) {
        flex-direction: ${props => props.maxWidthDirection};
    }
`;

export const TextRouteLink = styled(Link)`
    color: ${({ theme }) => theme.colors.coral[500]};
    text-decoration: none;
`;

export const TextRouteAnchor = styled.a`
    color: ${({ theme }) => theme.colors.coral[500]};
    text-decoration: none;
    cursor: pointer;
`;

export const HorizontalLine = styled.div`
    background: ${grey['300']};
    width: 100%;
    height: 1px;
    flex-shrink: 0;
`;

export const Bullet = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 0.6rem;
    width: 0.6rem;
    border-radius: 5rem;
    background: ${({ theme }) => theme.colors.grey[700]};
`;

export const PageTitle = styled(Text).attrs({ textVariant: 'h1' })`
    @media (max-width: ${({ theme }) => theme.breakpoints.S.max}px) {
        ${({ theme }) => getFontStyle({ theme, fontVariant: 'h2' })}
    }
`;

export const SectionTitle = styled(Text).attrs({ textVariant: 'h2', textWeight: 'Medium' })`
    @media (max-width: ${({ theme }) => theme.breakpoints.S.max}px) {
        ${({ theme }) => getFontStyle({ theme, fontVariant: 'h3', fontWeight: 'Medium' })}
    }
`;

export const lineClampStyle = (count: number) => css`
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-all;
    text-overflow: ellipsis;
    /* stylelint-disable */
    display: -webkit-box;
    -webkit-line-clamp: ${count};
    -webkit-box-orient: vertical;
    /* stylelint-enable */
`;

export const IconWrapper = styled.div<{ width?: string; height?: string }>`
    width: ${props => props.width || '3.2rem'};
    height: ${props => props.height || '3.2rem'};
`;

export const CoralText = styled(Text)`
    color: ${({ theme }) => theme.colors.coral[500]};
`;

export const GreyText = styled(Text)`
    color: ${({ theme }) => theme.colors.grey[700]};
`;

export const YellowText = styled(Text)`
    color: ${({ theme }) => theme.colors.yellow[500]};
`;

export const BadgeCount = styled(Text).attrs({ textVariant: 'body3', textWeight: 'Medium' })`
    ${centerAligned()}
    padding: 0.2rem 0.9rem;
    border-radius: 1.6rem;
    background-color: ${({ theme }) => theme.colors.darkBlue};
    color: ${({ theme }) => theme.colors.white};
`;

export const highlightCardStyle = css`
    &&& {
        border: 0.15rem solid ${({ theme }) => theme.colors.grey[700]};
        box-shadow: 0 0.2rem 1.6rem ${({ theme }) => rgba(theme.colors.grey[500], 0.6)};
    }
`;

export const MobileNavHeaderMinHeight = '5.6rem';

const Styled = {
    LoaderContainer,
    Overlay,
    NoBgBox,
    FlexBox,
    TextRouteLink,
    TextRouteAnchor,
    HorizontalLine,
    BadgeCount,
    Bullet
};

export default Styled;
